import React, { useState, useEffect, useCallback, useRef } from 'react';
import GetAppIcon from '@material-ui/icons/GetApp';
import ListIcon from '@material-ui/icons/List';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Alert } from '@this/src/components/shared/ui/feedbacks/alert';
import { Modal, ModalHeader, ModalBody } from '@this/src/components/shared/ui/feedbacks/modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@this/src/components/shared/ui/data_displays/table';
import { Fetcher, HTTPError } from '@this/src/util';
import type { CostAnalysisCsvDownloadResponseArgs } from '@this/src/domain/cost_analysis_csv_download';
import CostAnalysisCsvDownload, {
  convertCostAnalysisCsvDownloadResponseToArgs
} from '@this/src/domain/cost_analysis_csv_download';
import type { SearchParams } from './cost_analysis_search_area';

interface Props {
  getSearchParams: () => SearchParams;
}

type CsvDownloadCreateResponse = {
  action_id: number;
  message: string;
};

type CsvDownloadListResponse = {
  csv_downloads: CostAnalysisCsvDownloadResponseArgs[];
  email_addresses: string[];
};

const CSV_DOWNLOADS_PATH = '/organization/cost_analysis/csv_downloads';
const CSV_STATUS_CHECK_INTERVAL = 10000; // 10秒
const CSV_STATUS_MAX_RETRIES = 6; // 最大6回（1分間）
const CSV_STATUS_ERROR_MAX_RETRIES = 3; // エラー時の最大リトライ回数

const statusColor = (download: CostAnalysisCsvDownload): string => {
  if (download.status === 'failed') return '#dc3545';
  return download.downloadable ? '#28a745' : '#ffc107';
};

const CostAnalysisCsvDownloadArea: React.FC<Props> = ({ getSearchParams }) => {
  const [csvDownloads, setCsvDownloads] = useState<CostAnalysisCsvDownload[]>([]);
  const [emailAddresses, setEmailAddresses] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [refreshLoading, setRefreshLoading] = useState<boolean>(false);
  const [csvDownloadDisabled, setCsvDownloadDisabled] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const statusCheckTimer = useRef<number | null>(null);

  const fetchCsvDownloadList = async (): Promise<CostAnalysisCsvDownload[]> => {
    const response = await Fetcher.get<CsvDownloadListResponse>(CSV_DOWNLOADS_PATH);
    setEmailAddresses(response.email_addresses.join(', '));
    return response.csv_downloads.map(
      raw => new CostAnalysisCsvDownload(convertCostAnalysisCsvDownloadResponseToArgs(raw))
    );
  };

  const fetchCsvDownloads = useCallback(async () => {
    setRefreshLoading(true);
    try {
      setCsvDownloads(await fetchCsvDownloadList());
    } catch {
      setErrorMessage('ダウンロードデータ一覧の取得に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setRefreshLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCsvDownloads();
    return () => {
      if (statusCheckTimer.current) window.clearTimeout(statusCheckTimer.current);
    };
  }, [fetchCsvDownloads]);

  const scheduleStatusCheck = (actionId: number, retryCount: number) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    statusCheckTimer.current = window.setTimeout(
      () => checkCsvStatus(actionId, retryCount),
      CSV_STATUS_CHECK_INTERVAL
    );
  };

  const checkCsvStatus = async (actionId: number, retryCount = 0) => {
    try {
      const downloads = await fetchCsvDownloadList();
      setCsvDownloads(downloads);
      const target = downloads.find(download => download.id === actionId);

      if (target?.finished || retryCount >= CSV_STATUS_MAX_RETRIES) {
        setCsvDownloadDisabled(false);
        return;
      }
      scheduleStatusCheck(actionId, retryCount + 1);
    } catch {
      if (retryCount >= CSV_STATUS_ERROR_MAX_RETRIES) {
        setCsvDownloadDisabled(false);
        return;
      }
      scheduleStatusCheck(actionId, retryCount + 1);
    }
  };

  const handleCreateCsvDownload = async () => {
    setCreating(true);
    try {
      const response = await Fetcher.post<CsvDownloadCreateResponse>(CSV_DOWNLOADS_PATH, getSearchParams());
      setShowConfirmModal(false);
      setCsvDownloadDisabled(true);
      checkCsvStatus(response.action_id);
    } catch (e) {
      setShowConfirmModal(false);
      setErrorMessage(
        e instanceof HTTPError && e.response?.data?.error
          ? e.response.data.error
          : 'CSVダウンロードの準備に失敗しました。時間をおいて再度お試しください。'
      );
    } finally {
      setCreating(false);
    }
  };

  const handleOpenHistoryModal = () => {
    setShowHistoryModal(true);
    fetchCsvDownloads();
  };

  const handleDirectDownload = (download: CostAnalysisCsvDownload, encoding: 'utf8' | 'sjis') => {
    const downloadUrl = download.downloadUrls?.[encoding];
    if (downloadUrl) window.location.href = downloadUrl;
  };

  return (
    <div>
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage(null)} style={{ marginBottom: '10px' }}>
          {errorMessage}
        </Alert>
      )}
      <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px', marginBottom: '20px' }}>
        <Button color="sub" onClick={() => setShowConfirmModal(true)} disabled={csvDownloadDisabled}>
          <span style={{ marginRight: '5px' }}>CSVダウンロード</span>
          <GetAppIcon />
        </Button>
        {csvDownloads.length > 0 && (
          <Button color="sub" onClick={handleOpenHistoryModal}>
            <span style={{ marginRight: '5px' }}>ダウンロードデータ一覧</span>
            <ListIcon />
          </Button>
        )}
      </div>
      {showConfirmModal && (
        <Modal open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
          <ModalHeader>CSVダウンロード確認</ModalHeader>
          <ModalBody>
            <p>{emailAddresses}にCSVをメールで送信します。</p>
            <p>UTF-8とShift_JIS両方の形式のファイルを送信します。</p>
            <p>ファイルの送信が完了するまで、次のダウンロードはできません。</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', columnGap: '10px', marginTop: '20px' }}>
              <Button color="sub" onClick={() => setShowConfirmModal(false)} disabled={creating}>
                キャンセル
              </Button>
              <Button color="primary" onClick={handleCreateCsvDownload} disabled={creating}>
                {creating ? '準備中...' : '確認'}
              </Button>
            </div>
          </ModalBody>
        </Modal>
      )}
      {showHistoryModal && (
        <Modal open={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          <ModalHeader>帳票出力一覧</ModalHeader>
          <ModalBody>
            <p>帳票の出力に時間がかかる場合があります。</p>
            <p>「最新の出力状況を取得」ボタンを押すことで、最新の出力状況を取得できます。</p>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '15px 0' }}>
              <Button color="primary" onClick={fetchCsvDownloads} disabled={refreshLoading}>
                {refreshLoading ? '取得中...' : '最新の出力状況を取得'}
              </Button>
            </div>
            <div style={{ marginTop: '20px' }}>
              {csvDownloads.length === 0 ? (
                <p>履歴がありません</p>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>出力日</TableCell>
                      <TableCell>状態</TableCell>
                      <TableCell>ダウンロード</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvDownloads.map(download => (
                      <TableRow key={download.id}>
                        <TableCell>{download.createdAt}</TableCell>
                        <TableCell>
                          <span style={{ color: statusColor(download), fontWeight: 'bold' }}>
                            {download.statusStr}
                          </span>
                        </TableCell>
                        <TableCell>
                          {download.downloadable ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                              <Button
                                color="primary"
                                size="small"
                                onClick={() => handleDirectDownload(download, 'utf8')}
                              >
                                UTF-8
                              </Button>
                              <Button
                                color="primary"
                                size="small"
                                onClick={() => handleDirectDownload(download, 'sjis')}
                              >
                                Shift_JIS
                              </Button>
                            </div>
                          ) : (
                            <span style={{ color: '#6c757d' }}>
                              {download.status === 'failed' ? '-' : '準備中'}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <Button color="sub" onClick={() => setShowHistoryModal(false)}>
                閉じる
              </Button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

export default CostAnalysisCsvDownloadArea;
